import React from "react";
import { Box, Typography, IconButton } from "@mui/material";
import { ComoButton } from "../Common/ComoButton";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import color from "../../Constants/colors";
import { useTranslation } from "react-i18next";

interface NewFilesListProps {
  files: File[];
  onRemoveFile: (fileName: string) => void;
  onSubmit: () => void;
  isUploading: boolean;
}

const NewFilesList: React.FC<NewFilesListProps> = ({
  files,
  onRemoveFile,
  onSubmit,
  isUploading,
}) => {
  const { t } = useTranslation("documentUpload");

  if (files.length === 0) {
    return null;
  }

  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          marginTop: 1,
          marginBottom: "16px",
        }}
      >
        <Typography
          variant="subtitle2"
          sx={{
            fontSize: "0.875rem",
            fontWeight: 500,
            color: color.gray700,
            marginBottom: "8px",
          }}
        >
          {t("documentUpload.fileToUpload")} ({files.length})
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: "8px",
            overflowY: "auto",
          }}
        >
          {files.map((file, index) => (
            <Box
              key={index}
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: `${color.accent}10`,
                borderRadius: "4px",
                border: `1px solid ${color.orange50}`,
                padding: "10px 12px",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                <UploadFileIcon sx={{ color: color.accent, mr: 1 }} />
                <Typography
                  variant="body2"
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 500,
                    color: color.gray900,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: "0.7rem",
                    color: color.gray600,
                    ml: 1,
                    whiteSpace: "nowrap",
                  }}
                >
                  {(file.size / (1024 * 1024)).toFixed(2)} MB
                </Typography>
              </Box>
              <IconButton
                onClick={() => onRemoveFile(file.name)}
                size="small"
                sx={{
                  padding: "6px",
                  ml: 1,
                  "&:hover": {
                    backgroundColor: color.red50,
                  },
                }}
              >
                <CloseIcon sx={{ fontSize: "16px", color: color.red600 }} />
              </IconButton>
            </Box>
          ))}
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          marginTop: 2,
        }}
      >
        <ComoButton
          onClick={onSubmit}
          disabled={files.length === 0 || isUploading}
        >
          {t("documentUpload.upload")}
        </ComoButton>
      </Box>
    </>
  );
};

export default NewFilesList;
